/* ============================================================================
   mazo-facets.js — the ONE thing the collection facets need JS for: locking the page behind the mobile
   "Filter and sort" drawer.

   ⚠️ WHY THIS FILE HAS TO EXIST. Dawn locks the background by adding `overflow-hidden-mobile` to `<body>` from
   `global.js` for every `<menu-drawer>`, and `mazo-footer.css` §29b DELIBERATELY neutralises that class store-wide
   (`body.overflow-hidden-mobile{ overflow:visible !important }`) because setting `overflow:hidden` on the document
   destroys the scroll position and shifts the fixed header by the scrollbar width. In its place the store has one
   real lock, `mazo-scroll-lock.js`, which blocks the wheel/touch/key events instead — but its two callers were the
   cart drawer and the header's menu drawer. The FACET drawer is also a `<menu-drawer>`, so it got the lock removed
   and nothing put back.
   MEASURED at 390x844 with a trusted wheel: with the drawer open, `window.scrollY` went 184 -> 584 with the
   pointer over the scrim and on to 736 over the panel itself, while `<html>` never gained `mazo-scroll-locked`.
   A synthetic touch drag did NOT reproduce it (no fling velocity), so on a real phone the live path is the panel's
   own list chaining into the document once it is taller than the viewport — which is why `overscroll-behavior` is
   set in CSS as well. Both halves are needed.

   ⚠️ IT WATCHES THE `open` ATTRIBUTE, NOT A CLICK. Dawn's facet drawer is a `<details>` inside a `<menu-drawer>`
   and is opened from more than one place (the summary, the Escape key, and `facets.js` re-rendering the whole
   drawer after a filter is applied). An attribute observer catches every one of those, including the re-render,
   where a click handler would silently miss the new node.
   ⚠️ IT FAILS SOFT. If `window.mazoScrollLock` is missing the page simply scrolls as it does today.
   Styles: mazo-collection.css §39. Loaded from sections/main-collection-product-grid.liquid.
   ============================================================================ */
(function () {
  if (!window.MutationObserver) return;

  function panelFor(details) {
    return details.querySelector('.mobile-facets__inner') || details;
  }

  function sync(details) {
    if (!window.mazoScrollLock) return;
    var panel = panelFor(details);
    if (details.hasAttribute('open')) window.mazoScrollLock.enable(panel);
    else window.mazoScrollLock.disable(panel);
  }

  /* ==========================================================================
     ⚠️⚠️ DAWN NEVER REMOVES `open` FROM THE FACET DRAWER, AND THAT IS WHY IT CAN COME BACK.
     Owner, 2026-09-18e: "if i open filter and close, and immediately open cart, again filter panel is opening."
     MEASURED at 390px with real taps: after tapping the close disc the `<details>` still reports `open` at 0ms, 120ms
     and 400ms — every time. All Dawn does is drop the `menu-opening` class, and the panel is then hidden ONLY by
     `component-facets.css`'s `.js .disclosure-has-popup:not(.menu-opening) .mobile-facets__inner{ transform:
     translateX(105vw) }`. So the drawer spends the rest of the page's life in a state where it is open in the DOM and
     merely translated out of sight, and ANYTHING that puts `menu-opening` back — another drawer's focus handling, a
     `facets.js` re-render, a class written by a third-party script — slides it straight back in.
     The specific sequence the owner hit did not reproduce in three timings and two close paths, so rather than guess
     at the trigger this closes the state properly: once the close transition has had time to finish, the attribute
     goes. A `<details>` without `open` cannot be revealed by a stray class, which removes the whole family of
     "the panel came back" faults instead of one instance of it.
     ⚠️ IT WAITS FOR THE TRANSITION. Removing `open` immediately would delete the panel mid-slide and the close would
     jump. 420ms clears Dawn's 300ms transform with margin.
     ⚠️ IT CHECKS AGAIN BEFORE ACTING — if the shopper re-opened the drawer inside those 420ms, `menu-opening` is back
     and the attribute must stay.
     ========================================================================== */
  function closeFully(details) {
    if (details.__mazoCloseTimer) clearTimeout(details.__mazoCloseTimer);
    details.__mazoCloseTimer = setTimeout(function () {
      if (details.classList.contains('menu-opening')) return;   // re-opened in the meantime
      if (details.hasAttribute('open')) details.removeAttribute('open');
    }, 420);
  }

  function bind(root) {
    var list = (root || document).querySelectorAll('menu-drawer details, .mobile-facets__wrapper details');
    for (var i = 0; i < list.length; i++) {
      var d = list[i];
      if (d.__mazoFacetBound) continue;
      /* only the drawer shell, not the individual filter accordions inside it */
      if (!d.querySelector('.mobile-facets__inner')) continue;
      d.__mazoFacetBound = true;
      new MutationObserver(function (recs) {
        var t = recs[0].target;
        sync(t);
        /* `menu-opening` is Dawn's real open/closed signal; `open` outlives it */
        if (!t.classList.contains('menu-opening') && t.hasAttribute('open')) closeFully(t);
        else if (t.__mazoCloseTimer) clearTimeout(t.__mazoCloseTimer);
      }).observe(d, { attributes: true, attributeFilter: ['open', 'class'] });
      sync(d);
    }
  }

  bind(document);
  /* facets.js replaces the whole facet form after every filter change, so re-bind on any subtree swap */
  var queued = false;
  new MutationObserver(function () {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; bind(document); });
  }).observe(document.body, { childList: true, subtree: true });

  /* ==========================================================================
     ⚠️⚠️ THE REAL "FILTER PANEL COMES BACK" BUG: TWO FULL-SCREEN OVERLAYS WERE ALLOWED AT ONCE.
     Owner, three times, the last one exasperated: "the filter open close and cart icon click still opens filter panel
     is not fixed!!!"
     WHAT I HAD BEEN TESTING WAS THE WRONG PATH. Closing the drawer with its X and then tapping the cart works fine and
     always did — that is why two rounds of "cannot reproduce". Reproduced at 390px by testing every OTHER path:
       - **Tap the cart icon while the filter drawer is still open.** The drawer's trigger is behind nothing, so this is
         easy to do by accident, and both panels are full-bleed on a phone. The cart opens ON TOP of a filter panel that
         is still at `panelX=0`; close the cart and the filter is sitting there. Measured: `open=Y mo=Y panelX=0 cart=Y`.
       - **Tap "Remove all".** It is an `<a href>` that Dawn re-renders through, and unlike Apply it does NOT close the
         drawer. Same end state: `open=Y mo=Y panelX=0 cart=Y` once the cart opens over it.
     So it was never the `open` attribute lingering (that fix stands and is still right — see `closeFully`); it was that
     nothing dismissed the facet drawer when another overlay took the screen.
     THE RULE NOW: the cart drawer is the store's topmost overlay, and opening it dismisses the facet drawer.
     ⚠️ THE SCROLL LOCK SURVIVES THIS, and that is only true because `mazo-scroll-lock.js` keeps a STACK — `disable()`
     returns early while any other panel is still on it, so releasing the facet panel cannot unlock the page under an
     open cart. Do not "simplify" that lock to a boolean.
     ⚠️ NO `.focus()` ON THE WAY OUT. Dawn's own `closeMenuDrawer` focuses the summary it came from, which would scroll
     the collection page behind the cart that is opening. The class comes off, `closeFully` strips `open` after the
     transition, and the attribute observer releases that panel's lock.
     ========================================================================== */
  function dismissFacetDrawers() {
    var list = document.querySelectorAll('.mobile-facets__wrapper details, menu-drawer details');
    for (var i = 0; i < list.length; i++) {
      var d = list[i];
      if (!d.querySelector('.mobile-facets__inner')) continue;      /* the drawer shell only */
      if (!d.classList.contains('menu-opening') && !d.hasAttribute('open')) continue;
      d.classList.remove('menu-opening');
      closeFully(d);
    }
  }

  var cartEl = document.querySelector('cart-drawer');
  if (cartEl) {
    new MutationObserver(function () {
      if (cartEl.classList.contains('active')) dismissFacetDrawers();
    }).observe(cartEl, { attributes: true, attributeFilter: ['class'] });
  }

  /* "Remove all" closes the drawer, because Apply does and they are the same kind of action — a footer button that
     submits and hands the shopper back to the results. Leaving one open and one closed was also the second way into
     the overlay collision above. */
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    if (!e.target.closest('.mobile-facets__clear')) return;
    var d = e.target.closest('.mobile-facets__wrapper details, menu-drawer details');
    if (!d) return;
    d.classList.remove('menu-opening');
    closeFully(d);
  });

  /* ==========================================================================
     THE DESKTOP FILTER / SORT DISCLOSURES: ONE OPEN AT A TIME, AND DISMISS ON AN OUTSIDE CLICK.

     ⚠️ THIS IS THE OTHER HALF OF mazo-collection.css §39m, AND NEITHER HALF WORKS ALONE. Dawn dismisses an open
     filter popup with zero JavaScript by hanging a `position:fixed; inset:0` shield off the open `<summary>`
     (`base.css:687`), so every click in the viewport is a click on that summary and toggles it shut. The cost is
     that a click on a SECOND filter can only ever close the first — the owner's "another filter cannot be opened,
     i have to first close the first already open filter". §39m removes the shield for the facet disclosures only;
     these two listeners put the dismissal back, and let a click go straight from one trigger to the next.

     ⚠️ DELEGATED ON `document`, NOT BOUND PER SUMMARY. `facets.js` re-renders the entire facet form after every
     filter change, so any handler attached to a summary is thrown away with the node it was on. Same reason the
     drawer watcher above observes an attribute instead of listening for a click.
     ⚠️ THE OTHERS ARE CLOSED ON `mousedown`, BEFORE the browser's own toggle runs on `click`. Doing it on `click`
     would close them AFTER the newly clicked panel had already been laid out, which reflows the row under the
     pointer — the class of problem that produced the dead-click in the first place.
     ⚠️ THE MOBILE DRAWER'S ACCORDIONS ARE EXCLUDED. Those are a stacked list inside the panel where more than one
     open section is normal and closing one on opening another would hide the shopper's own earlier choice.
     ========================================================================== */
  function desktopDisclosures() {
    return document.querySelectorAll('.facets__disclosure.disclosure-has-popup');
  }

  function closeAll(except) {
    var list = desktopDisclosures();
    for (var i = 0; i < list.length; i++) {
      if (list[i] !== except && list[i].hasAttribute('open')) list[i].removeAttribute('open');
    }
  }

  document.addEventListener('mousedown', function (e) {
    var summary = e.target.closest && e.target.closest('.facets__disclosure.disclosure-has-popup > summary');
    if (summary) { closeAll(summary.parentElement); return; }
    /* a press anywhere outside an open panel dismisses every one of them */
    if (!(e.target.closest && e.target.closest('.facets__disclosure.disclosure-has-popup'))) closeAll(null);
  }, true);

  /* Escape: Dawn only wires this up for details that carry an id starting "Details-", which the facet disclosures
     do not, so it never worked here. */
  document.addEventListener('keyup', function (e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;
    var open = document.querySelector('.facets__disclosure.disclosure-has-popup[open]');
    if (!open) return;
    closeAll(null);
    var s = open.querySelector('summary');
    if (s) s.focus();
  });
})();
